/**
 * StoryPicker — mandatory topic selection before starting a session.
 *
 * Promotes and simplifies SuggestionBanner: no skip, no multi-view state,
 * just a flat list of pending/captured topics grouped by area plus a free-form option.
 *
 * Requirements: PARITY-04
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCurriculum, type CurriculumArea } from '../../lib/curriculum-api';
import { fetchStoryTopics, type StoryTopic } from '../../lib/story-topics-api';

interface Props {
  onAccept: (areaId: string, topicId?: string, topicTitle?: string) => void;
}

export function StoryPicker({ onAccept }: Props) {
  const navigate = useNavigate();
  const [areas, setAreas] = useState<CurriculumArea[]>([]);
  const [topics, setTopics] = useState<StoryTopic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const [topicsResult, curriculumResult] = await Promise.allSettled([
        fetchStoryTopics(),
        fetchCurriculum(),
      ]);
      if (cancelled) return;

      const allTopics = topicsResult.status === 'fulfilled' ? topicsResult.value : [];
      const allAreas = curriculumResult.status === 'fulfilled' ? curriculumResult.value.areas : [];

      // Only show pending + captured topics
      setTopics(allTopics.filter((t) => t.status === 'pending' || t.status === 'captured'));
      setAreas(allAreas);
      setLoading(false);
    }

    void load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="w-full space-y-2">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-10 bg-stone-100 rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  // Group topics by areaId
  const topicsByArea: Array<{ area: CurriculumArea; areaTopics: StoryTopic[] }> = areas
    .map((area) => ({ area, areaTopics: topics.filter((t) => t.areaId === area.id) }))
    .filter(({ areaTopics }) => areaTopics.length > 0);

  return (
    <div className="w-full space-y-4">
      <h3 className="text-xl font-semibold text-stone-900">
        Which story do you want to tell today?
      </h3>

      {topicsByArea.length === 0 ? (
        <div className="text-center py-4 space-y-2">
          <p className="text-sm text-stone-500">Set up your story plan to see topic suggestions.</p>
          <button
            type="button"
            onClick={() => navigate('/stories')}
            className="text-sm text-amber-600 font-semibold hover:underline"
          >
            Go to My Stories →
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {topicsByArea.map(({ area, areaTopics }) => (
            <div key={area.id}>
              <p className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-2">
                {area.title}
              </p>
              <div className="space-y-1">
                {areaTopics.map((topic) => {
                  const isCaptured = topic.status === 'captured' || topic.status === 'complete';
                  return (
                    <button
                      key={topic.id}
                      type="button"
                      onClick={() => onAccept(area.id, topic.id, topic.title)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-stone-200 bg-white hover:border-amber-400 hover:bg-amber-50 transition-all text-left focus:outline-none focus:ring-2 focus:ring-amber-400"
                    >
                      <span
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${isCaptured ? 'bg-green-500' : 'bg-amber-400'}`}
                        aria-label={topic.status}
                      />
                      <span className="text-sm text-stone-800">{topic.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Free-form option — always last */}
      <div className="pt-2 border-t border-stone-100">
        <button
          type="button"
          onClick={() => onAccept('', undefined, undefined)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-stone-300 bg-stone-50 hover:border-amber-400 hover:bg-amber-50 transition-all text-left focus:outline-none focus:ring-2 focus:ring-amber-400"
        >
          <span className="text-sm text-stone-500 italic">No specific story — let Claude guide me</span>
        </button>
      </div>
    </div>
  );
}
